import * as pdfjsLib from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

// Worker-Setup identisch zu PdfCropper/UfsaEntryForm: aus dem Bundle, nicht von
// unpkg. Die fest verdrahtete CDN-Version passte nach einem Update nicht mehr zur
// lokalen Bibliothek ("API version does not match Worker version").
// Nur setzen, falls noch nicht konfiguriert.
if (pdfjsLib.GlobalWorkerOptions && !pdfjsLib.GlobalWorkerOptions.workerSrc) {
  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl as string;
}

// Zielbreite der gerenderten A4-Seite: ~150 DPI bei 210 mm Breite ≈ 1240 px.
const TARGET_WIDTH = 1240;

// Rendert eine pdf.js-Seite auf ein Canvas und liefert sie als JPEG-dataURL.
async function renderPdfPageToJpeg(page: any): Promise<string> {
  const base = page.getViewport({ scale: 1 });
  const scale = TARGET_WIDTH / base.width;
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas-Kontext nicht verfügbar.');

  // Weißer Hintergrund, damit transparente Bereiche im JPEG nicht schwarz werden.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  await page.render({ canvasContext: ctx, viewport }).promise;
  return canvas.toDataURL('image/jpeg', 0.82);
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden.'));
    reader.readAsDataURL(file);
  });
}

/**
 * Wandelt eine hochgeladene Briefpapier-Datei in eine (oder zwei) Bild-dataURLs um.
 *
 *  - PDF: rendert Seite 1 (und Seite 2, falls vorhanden) via pdf.js zu JPEG (~150 DPI, A4).
 *  - Bild (PNG/JPEG): wird unverändert als dataURL zurückgegeben (nur page1).
 *  - Andere Typen oder Render-Fehler: Reject mit deutscher Fehlermeldung.
 */
export async function fileToStationeryImage(
  file: File,
): Promise<{ page1: string; page2?: string }> {
  const name = (file.name || '').toLowerCase();
  const isPdf = file.type === 'application/pdf' || name.endsWith('.pdf');
  const isImage = /^image\/(png|jpe?g)$/.test(file.type) || /\.(png|jpe?g)$/.test(name);

  if (isPdf) {
    try {
      const data = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data }).promise;
      const page1 = await renderPdfPageToJpeg(await pdf.getPage(1));
      let page2: string | undefined;
      if (pdf.numPages >= 2) {
        page2 = await renderPdfPageToJpeg(await pdf.getPage(2));
      }
      return page2 ? { page1, page2 } : { page1 };
    } catch (e) {
      console.error('Briefpapier-PDF konnte nicht gerendert werden', e);
      throw new Error(
        'Briefpapier-PDF konnte nicht verarbeitet werden. Bitte eine gültige PDF-Datei wählen.',
      );
    }
  }

  if (isImage) {
    const page1 = await fileToDataUrl(file);
    return { page1 };
  }

  throw new Error(
    'Nicht unterstütztes Format. Bitte PDF, PNG oder JPEG als Briefpapier hochladen.',
  );
}
